/**
 * Pathname of a history/link url, resolved against the current origin.
 *
 * @param {string|null|undefined} url
 * @returns {string|null} null when there is no url or it cannot be parsed.
 */
export const toPathname = (url) => {
  if (url == null) return null;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }
};

/**
 * The internal href a click is about to make the browser follow in this tab, or null when the
 * browser will not follow it here: a handler took the click over (a router Link or the dock's
 * links prevent the default and navigate through history), it asks for another tab or a download,
 * or the link leaves the app.
 *
 * @param {MouseEvent} event
 * @returns {string|null}
 */
export const followedLinkHref = (event) => {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const link = event.target?.closest?.('a[href]');
  if (!link || link.hasAttribute('download')) return null;
  const target = link.getAttribute('target');
  if (target && target !== '_self') return null;
  const href = link.getAttribute('href');
  return href.startsWith('/') && !href.startsWith('//') ? href : null;
};

/**
 * Installs interceptors over `history.pushState` / `history.replaceState`.
 *
 * Each interceptor receives the call's arguments and returns true to swallow the
 * navigation, false to let it reach the underlying implementation.
 *
 * Patches nest: a dirty Modal inside a dirty Stepper installs one each, and
 * teardown is not guaranteed to be LIFO — the Stepper going clean while the
 * Modal is still open tears the outer one down first. `uninstall` therefore
 * restores only while its own patch is the one currently installed. An inner
 * patch that outlives its outer one keeps intercepting, and the orphaned outer
 * patch degrades to a pass-through once its interceptor stops swallowing.
 *
 * `natives` carries the pre-patch implementations forward across re-installs so
 * a caller that arms, disarms and re-arms always executes confirmed navigation
 * against the real browser methods rather than another patch.
 *
 * @param {{
 *   natives?: { pushState: Function, replaceState: Function } | null,
 *   onPushState: (args: any[]) => boolean,
 *   onReplaceState: (args: any[]) => boolean
 * }} config
 */
export const installHistoryPatch = ({ natives, onPushState, onReplaceState }) => {
  const nativePushState = natives?.pushState || window.history.pushState;
  const nativeReplaceState = natives?.replaceState || window.history.replaceState;

  const patchedPushState = function (...args) {
    if (onPushState(args)) return undefined;
    return nativePushState.apply(this, args);
  };

  const patchedReplaceState = function (...args) {
    if (onReplaceState(args)) return undefined;
    return nativeReplaceState.apply(this, args);
  };

  window.history.pushState = patchedPushState;
  window.history.replaceState = patchedReplaceState;

  return {
    natives: { pushState: nativePushState, replaceState: nativeReplaceState },
    uninstall: () => {
      if (window.history.pushState === patchedPushState) {
        window.history.pushState = nativePushState;
      }
      if (window.history.replaceState === patchedReplaceState) {
        window.history.replaceState = nativeReplaceState;
      }
    }
  };
};

export default installHistoryPatch;
